/**
 * Site Detail Page
 * Comprehensive view of a single site project from the Site Database
 * Includes phase tracking, task management, and progress visualization
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { siteDatabaseAPI } from '../services/api';
import { toast } from 'react-toastify';
import {
  SITE_STATUS,
  SITE_STATUS_LABELS,
  SITE_STATUS_COLORS
} from '../utils/constants';
import { hasDirectorAccess } from '../utils/rolePermissions';
import { toTitleCase } from '../utils/formatters';

// Import site components
import {
  SitePhaseProgress,
  SiteQuickActionButtons,
  SiteHistoryTimeline,
  CompletePhaseModal,
  SkipPhaseModal,
  AddPhaseModal,
  UpdateProgressModal,
  ChangeSiteStatusModal,
  EditPhaseModal,
  SetupPhasesModal,
  AssignSiteTeamModal,
  ConsolidatedAuditTrail,
  AddSiteNoteModal,
  EditSiteNotesModal,
  SiteWorkflowNotes
} from '../components/site';
import AttendanceModal from '../components/site/AttendanceModal';
import AttendanceRecords from '../components/site/AttendanceRecords';
import CollapsibleSection from '../components/common/CollapsibleSection';
import PaymentMilestonesSection from '../components/projects/PaymentMilestonesSection';

// Format currency
function formatCurrency(amount) {
  if (!amount) return 'N/A';
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

// Format date
function formatDate(dateString) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-MY', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

// Format property type
function formatPropertyType(type) {
  if (!type) return 'N/A';
  return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

export default function SiteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Back-link target: explicit `from` URL set by the caller (preserves their
  // filters/search via query string). Falls back to history pop, then a
  // hard-coded site-database URL for direct/refresh loads.
  const backFrom = location.state?.from;
  const backLabel = location.state?.fromLabel || 'Site Database';
  const handleBack = () => {
    if (backFrom) navigate(backFrom);
    else if (location.key !== 'default') navigate(-1);
    else navigate('/dashboard?tab=site-database');
  };

  // Main data state
  const [site, setSite] = useState(null);
  const [phases, setPhases] = useState([]);
  const [history, setHistory] = useState([]);
  const [phaseTemplates, setPhaseTemplates] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [phasesLoading, setPhasesLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Error states
  const [error, setError] = useState(null);
  const [phasesError, setPhasesError] = useState(null);
  const [historyError, setHistoryError] = useState(null);

  // Modal states
  const [completePhaseModal, setCompletePhaseModal] = useState({ open: false, phase: null });
  const [skipPhaseModal, setSkipPhaseModal] = useState({ open: false, phase: null });
  const [addPhaseModal, setAddPhaseModal] = useState(false);
  const [updateProgressModal, setUpdateProgressModal] = useState({ open: false, phase: null });
  const [changeStatusModal, setChangeStatusModal] = useState({ open: false, targetStatus: null });
  const [editPhaseModal, setEditPhaseModal] = useState({ open: false, phase: null });
  const [setupPhasesModal, setSetupPhasesModal] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);

  // Attendance state
  const [attendanceModal, setAttendanceModal] = useState({ open: false, type: null });
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [attendanceRefresh, setAttendanceRefresh] = useState(0);

  // Site team assignment state
  const [assignTeamModal, setAssignTeamModal] = useState(false);

  // Site notes modals
  const [addNoteModal, setAddNoteModal] = useState(false);
  const [editNotesModal, setEditNotesModal] = useState(false);

  // Attendance records for audit trail
  const [attendanceRecords, setAttendanceRecords] = useState([]);

  // Check if user can edit site (includes check-in/check-out)
  const canEdit = hasDirectorAccess(user?.role) || user?.role === 'manager' ||
    user?.role === 'site_manager' || user?.role === 'site_supervisor' ||
    user?.role === 'sid' || user?.role === 'asid' || user?.role === 'operation_head';

  // Check if user can assign site team (managers, supervisors)
  const canAssignTeam = hasDirectorAccess(user?.role) || user?.role === 'manager' ||
    user?.role === 'site_manager' || user?.role === 'operation_head';

  // Check if user can view design project
  const canViewDesignProject = hasDirectorAccess(user?.role) || user?.role === 'manager' ||
    user?.role === 'leader' || user?.role === 'sid' || user?.role === 'asid';

  // Fetch site data
  const fetchSiteData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await siteDatabaseAPI.get(id);
      setSite(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching site:', err);
      setError(err.response?.data?.error || 'Failed to load site details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch phases
  const fetchPhases = useCallback(async () => {
    try {
      setPhasesLoading(true);
      setPhasesError(null);
      const response = await siteDatabaseAPI.getPhases(id);
      setPhases(response.data.data || []);
    } catch (err) {
      console.error('Error fetching phases:', err);
      setPhasesError('Failed to load phases. Please try again.');
    } finally {
      setPhasesLoading(false);
    }
  }, [id]);

  // Fetch history
  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      setHistoryError(null);
      const response = await siteDatabaseAPI.getHistory(id);
      setHistory(response.data.data || []);
    } catch (err) {
      console.error('Error fetching history:', err);
      setHistoryError('Failed to load history. Please try again.');
    } finally {
      setHistoryLoading(false);
    }
  }, [id]);

  // Fetch attendance records for audit trail
  const fetchAttendanceRecords = useCallback(async () => {
    try {
      const response = await siteDatabaseAPI.getAttendance(id, { limit: 100 });
      setAttendanceRecords(response.data.data || []);
    } catch (err) {
      console.error('Error fetching attendance records:', err);
    }
  }, [id]);

  // Fetch phase templates
  const fetchPhaseTemplates = useCallback(async () => {
    try {
      const response = await siteDatabaseAPI.getPhaseTemplates();
      setPhaseTemplates(response.data.data || {});
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  }, []);

  // Fetch attendance status
  const fetchAttendanceStatus = useCallback(async () => {
    if (!canEdit) return; // Only fetch for users who can record attendance
    try {
      const response = await siteDatabaseAPI.getTodayAttendanceStatus(id);
      setAttendanceStatus(response.data.data);
    } catch (err) {
      console.error('Error fetching attendance status:', err);
    }
  }, [id, canEdit]);

  // Initial data load
  useEffect(() => {
    if (id) {
      fetchSiteData();
      fetchPhases();
      fetchHistory();
      fetchPhaseTemplates();
      fetchAttendanceStatus();
      fetchAttendanceRecords();
    }
  }, [id, fetchSiteData, fetchPhases, fetchHistory, fetchPhaseTemplates, fetchAttendanceStatus, fetchAttendanceRecords]);

  // Refresh all data
  const refreshData = useCallback(() => {
    fetchSiteData();
    fetchPhases();
    fetchHistory();
    fetchAttendanceRecords();
  }, [fetchSiteData, fetchPhases, fetchHistory, fetchAttendanceRecords]);

  // === Phase Actions ===

  const handleStartPhase = async (phaseId) => {
    try {
      setActionLoading(true);
      await siteDatabaseAPI.startPhase(id, phaseId);
      toast.success('Phase started successfully');
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start phase');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompletePhase = (phaseId) => {
    const phase = phases.find(p => p.id === phaseId);
    setCompletePhaseModal({ open: true, phase });
  };

  const handleCompletePhaseConfirm = async ({ notes }) => {
    try {
      setActionLoading(true);
      await siteDatabaseAPI.completePhase(id, completePhaseModal.phase.id, { notes });
      toast.success('Phase completed successfully');
      setCompletePhaseModal({ open: false, phase: null });
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to complete phase');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSkipPhase = (phaseId) => {
    const phase = phases.find(p => p.id === phaseId);
    setSkipPhaseModal({ open: true, phase });
  };

  const handleSkipPhaseConfirm = async ({ reason }) => {
    try {
      setActionLoading(true);
      await siteDatabaseAPI.skipPhase(id, skipPhaseModal.phase.id, { reason });
      toast.success('Phase skipped');
      setSkipPhaseModal({ open: false, phase: null });
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to skip phase');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateProgress = (phaseId) => {
    const phase = phases.find(p => p.id === phaseId);
    setUpdateProgressModal({ open: true, phase });
  };

  const handleUpdateProgressConfirm = async ({ progress, notes }) => {
    try {
      setActionLoading(true);
      await siteDatabaseAPI.updatePhaseProgress(id, updateProgressModal.phase.id, { progress, notes });
      toast.success('Progress updated');
      setUpdateProgressModal({ open: false, phase: null });
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update progress');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddPhase = () => {
    setAddPhaseModal(true);
  };

  const handleAddPhaseConfirm = async (phaseData) => {
    try {
      setActionLoading(true);
      await siteDatabaseAPI.addPhase(id, phaseData);
      toast.success('Phase added successfully');
      setAddPhaseModal(false);
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add phase');
    } finally {
      setActionLoading(false);
    }
  };

  // === Status Actions ===

  const handleChangeStatus = (targetStatus) => {
    setChangeStatusModal({ open: true, targetStatus });
  };

  const handleChangeStatusConfirm = async ({ status, reason }) => {
    try {
      setActionLoading(true);
      await siteDatabaseAPI.updateStatus(id, status, reason);
      toast.success('Site status updated');
      setChangeStatusModal({ open: false, targetStatus: null });
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  // Edit phase handler
  const handleEditPhase = (phaseId) => {
    const phase = phases.find(p => p.id === phaseId);
    if (phase) {
      setEditPhaseModal({ open: true, phase });
    }
  };

  // Edit phase confirm handler
  const handleEditPhaseConfirm = async (data) => {
    try {
      setActionLoading(true);
      await siteDatabaseAPI.updatePhase(id, editPhaseModal.phase.id, data);
      toast.success('Phase updated successfully');
      setEditPhaseModal({ open: false, phase: null });
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update phase');
    } finally {
      setActionLoading(false);
    }
  };

  // Delete phase handler
  const handleDeletePhase = async (phaseId) => {
    try {
      setActionLoading(true);
      await siteDatabaseAPI.removePhase(id, phaseId);
      toast.success('Phase deleted successfully');
      setEditPhaseModal({ open: false, phase: null });
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete phase');
    } finally {
      setActionLoading(false);
    }
  };

  // Setup phases handler
  const handleSetupPhases = () => {
    setSetupPhasesModal(true);
  };

  // Setup phases confirm handler
  const handleSetupPhasesConfirm = async (phasesData) => {
    try {
      setActionLoading(true);
      await siteDatabaseAPI.setupPhases(id, phasesData);
      toast.success('Phases configured successfully');
      setSetupPhasesModal(false);
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to setup phases');
    } finally {
      setActionLoading(false);
    }
  };

  // === Task Actions ===

  const handleCreateTask = async (siteId, phaseId, title) => {
    try {
      await siteDatabaseAPI.createTask(siteId, phaseId, { title });
      toast.success('Task created');
      fetchPhases();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create task');
      throw err;
    }
  };

  const handleCompleteTask = async (siteId, taskId) => {
    try {
      await siteDatabaseAPI.completeTask(siteId, taskId);
      fetchPhases();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to complete task');
      throw err;
    }
  };

  const handleUncompleteTask = async (siteId, taskId) => {
    try {
      await siteDatabaseAPI.uncompleteTask(siteId, taskId);
      fetchPhases();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to uncomplete task');
      throw err;
    }
  };

  const handleDeleteTask = async (siteId, taskId) => {
    try {
      await siteDatabaseAPI.deleteTask(siteId, taskId);
      toast.success('Task deleted');
      fetchPhases();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete task');
      throw err;
    }
  };

  // === Note Actions ===

  const handleAddNote = async ({ noteType, content }) => {
    try {
      setActionLoading(true);
      switch (noteType) {
        case 'internal':
          await siteDatabaseAPI.updateInternalNotes(id, content);
          break;
        case 'handover':
          await siteDatabaseAPI.updateHandoverNotes(id, content);
          break;
        case 'clientFeedback':
          await siteDatabaseAPI.updateClientFeedback(id, content);
          break;
        default:
          throw new Error('Invalid note type');
      }
      toast.success('Note added successfully');
      setAddNoteModal(false);
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add note');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditNotes = async (changes) => {
    try {
      setActionLoading(true);
      const promises = [];

      if (changes.handoverNotes !== undefined) {
        promises.push(siteDatabaseAPI.updateHandoverNotes(id, changes.handoverNotes));
      }
      if (changes.internalNotes !== undefined) {
        promises.push(siteDatabaseAPI.updateInternalNotes(id, changes.internalNotes));
      }
      if (changes.clientFeedback !== undefined) {
        promises.push(siteDatabaseAPI.updateClientFeedback(id, changes.clientFeedback));
      }

      await Promise.all(promises);
      toast.success('Notes updated successfully');
      setEditNotesModal(false);
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update notes');
    } finally {
      setActionLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Site</h3>
          <p className="text-sm text-red-600">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Not found state
  if (!site) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Site not found</h3>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Get templates for current project type
  const currentTemplates = phaseTemplates[site.projectType] || phaseTemplates.renovation || [];

  return (
    <div className="min-h-screen bg-gray-50 pb-24 sm:pb-0">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Back button */}
          <button
            onClick={handleBack}
            className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to {backLabel}
          </button>

          {/* Site header */}
          <div className="flex flex-col gap-4">
            {/* Top row: Title + Utility buttons */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {site.projectNumber}
                </h1>
                <p className="mt-2 text-lg text-gray-600">{toTitleCase(site.clientName)}</p>
                <div className="mt-3 flex items-center gap-3">
                  <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                    site.siteStatus === SITE_STATUS.PENDING_SS_ASSIGNMENT ? 'bg-yellow-100 text-yellow-800' :
                    site.siteStatus === SITE_STATUS.ACTIVE ? 'bg-blue-100 text-blue-800' :
                    site.siteStatus === SITE_STATUS.ON_HOLD ? 'bg-gray-100 text-gray-800' :
                    site.siteStatus === SITE_STATUS.COMPLETED ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {SITE_STATUS_LABELS[site.siteStatus] || site.siteStatus}
                  </span>
                  {site.projectType && (
                    <span className="text-sm text-gray-500">
                      {site.projectType === 'renovation' ? 'Renovation' : 'Build from Land'}
                    </span>
                  )}
                </div>
              </div>

              {/* Utility buttons - Design Project & History */}
              <div className="flex items-center gap-2">
                {canViewDesignProject && site.designProjectId && (
                  <button
                    onClick={() => navigate(`/projects/${site.designProjectId}`)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 min-h-[40px]"
                    title="View Design Project"
                  >
                    <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    <span className="hidden sm:inline">Design Project</span>
                  </button>
                )}
                <button
                  onClick={() => setShowHistoryPanel(true)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 min-h-[40px]"
                  title="View History"
                >
                  <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="hidden sm:inline">History</span>
                  {history.length > 0 && (
                    <span className="ml-1 sm:ml-2 bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full text-xs font-medium">
                      {history.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Bottom row: Quick Actions - hidden on mobile */}
            <div className="hidden sm:flex items-center justify-between pt-4 border-t border-gray-200">
              {/* Primary Actions */}
              <div className="flex items-center gap-3">
                {/* Attendance Button */}
                {canEdit && attendanceStatus && (
                  <>
                    {attendanceStatus.canCheckIn && (
                      <button
                        onClick={() => setAttendanceModal({ open: true, type: 'check_in' })}
                        className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-green-700 ring-2 ring-green-600/20 ring-offset-1"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                        </svg>
                        Check In
                      </button>
                    )}
                    {attendanceStatus.canCheckOut && (
                      <button
                        onClick={() => setAttendanceModal({ open: true, type: 'check_out' })}
                        className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-red-700"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Check Out
                      </button>
                    )}
                  </>
                )}

                {/* Phase Actions */}
                <SiteQuickActionButtons
                  site={site}
                  phases={phases}
                  userRole={user?.role}
                  onStartPhase={handleStartPhase}
                  onCompletePhase={handleCompletePhase}
                  onSkipPhase={handleSkipPhase}
                  onUpdateProgress={handleUpdateProgress}
                  onAddPhase={handleAddPhase}
                  onChangeStatus={handleChangeStatus}
                  onAssignTeam={() => setAssignTeamModal(true)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending SS Assignment Warning Banner */}
      {site.siteStatus === SITE_STATUS.PENDING_SS_ASSIGNMENT && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <p className="font-medium text-yellow-800">Site Supervisor Required</p>
                <p className="text-sm text-yellow-700">Assign a Site Supervisor to activate this site and start tracking phases.</p>
              </div>
              {canAssignTeam && (
                <button
                  onClick={() => setAssignTeamModal(true)}
                  className="flex-shrink-0 px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 transition-colors"
                >
                  Assign Now
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column - Progress & Phases */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Phase Progress Panel */}
            <SitePhaseProgress
              site={site}
              phases={phases}
              onPhaseClick={(phaseId) => {
                // Could expand phase details or navigate
              }}
              onStartPhase={handleStartPhase}
              onCompletePhase={handleCompletePhase}
              onSkipPhase={handleSkipPhase}
              onUpdateProgress={handleUpdateProgress}
              onEditPhase={handleEditPhase}
              onSetupPhases={handleSetupPhases}
              canEdit={canEdit}
              loading={phasesLoading}
              error={phasesError}
              onRetry={fetchPhases}
            />

            {/* Payment Milestones */}
            {site.designProjectId && (
              <PaymentMilestonesSection
                project={{
                  id: site.designProjectId,
                  workflowStatus: 'project_closed_won'
                }}
              />
            )}

            {/* Notes - Elevated to left column for visibility */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
                <div className="flex gap-2">
                  {canEdit && (
                    <>
                      <button
                        onClick={() => setAddNoteModal(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium min-h-[36px] px-2 touch-manipulation"
                      >
                        + Add
                      </button>
                      {(site.handoverNotes || site.internalNotes || site.clientFeedback) && (
                        <button
                          onClick={() => setEditNotesModal(true)}
                          className="text-sm text-gray-600 hover:text-gray-700 font-medium min-h-[36px] px-2 touch-manipulation"
                        >
                          Edit
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {site.handoverNotes || site.internalNotes || site.clientFeedback ? (
                <div className="space-y-4">
                  {site.handoverNotes && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Handover Notes</label>
                      <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{site.handoverNotes}</p>
                    </div>
                  )}
                  {site.internalNotes && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Internal Notes</label>
                      <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{site.internalNotes}</p>
                    </div>
                  )}
                  {site.clientFeedback && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Client Feedback</label>
                      <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{site.clientFeedback}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No notes added yet</p>
              )}
            </div>

            {/* Workflow Notes - Important updates from history */}
            <SiteWorkflowNotes
              history={history}
              loading={historyLoading}
            />

            {/* Client Information - Collapsible */}
            <CollapsibleSection title="Client Information" defaultExpanded={false}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Name</label>
                  <p className="mt-1 text-sm text-gray-900">{toTitleCase(site.clientName)}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</label>
                  <p className="mt-1 text-sm text-gray-900">{site.clientPhone}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{site.clientEmail || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">IC Number</label>
                  <p className="mt-1 text-sm text-gray-900">{site.clientIC || 'N/A'}</p>
                </div>
              </div>
            </CollapsibleSection>

            {/* Property Information - Collapsible */}
            <CollapsibleSection title="Property Details" defaultExpanded={false}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Address</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {site.propertyAddress || [site.addressLine1, site.addressLine2].filter(Boolean).join(', ') || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">City</label>
                  <p className="mt-1 text-sm text-gray-900">{site.city || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">State</label>
                  <p className="mt-1 text-sm text-gray-900">{site.state || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Property Type</label>
                  <p className="mt-1 text-sm text-gray-900">{formatPropertyType(site.propertyType)}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Property Size</label>
                  <p className="mt-1 text-sm text-gray-900">{site.propertySize ? `${site.propertySize} sqft` : 'N/A'}</p>
                </div>
              </div>
            </CollapsibleSection>

            {/* Project Scope */}
            {(site.projectScope || site.specialRequirements) && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Scope</h2>
                {site.projectScope && (
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Scope</label>
                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{site.projectScope}</p>
                  </div>
                )}
                {site.specialRequirements && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Special Requirements</label>
                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{site.specialRequirements}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-4 sm:space-y-6">
            {/* Attendance Records */}
            <AttendanceRecords
              siteId={id}
              refreshTrigger={attendanceRefresh}
            />

            {/* Activity History */}
            <SiteHistoryTimeline
              history={history}
              loading={historyLoading}
              maxItems={5}
              onShowMore={() => setShowHistoryPanel(true)}
            />

            {/* Team */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Team</h2>
                {canAssignTeam && (
                  <button
                    onClick={() => setAssignTeamModal(true)}
                    className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100"
                  >
                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Assign
                  </button>
                )}
              </div>
              <div className="space-y-4">
                {/* Project Lead (SID or ASID) */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Project Lead</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {site.sidName ? (
                      <>
                        {site.sidName}
                        <span className="ml-1.5 text-xs text-gray-500">
                          ({site.sidRoleType?.toUpperCase() || 'N/A'})
                        </span>
                      </>
                    ) : (
                      'Not Assigned'
                    )}
                  </p>
                </div>

                {/* ASID - only shown if there's a separate ASID (when project has both SID and ASID) */}
                {site.asidName && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">ASID</label>
                    <p className="mt-1 text-sm text-gray-900">{site.asidName}</p>
                  </div>
                )}

                {/* Site Manager */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Site Manager</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {site.siteManager?.nickname || site.siteManager?.fullName || site.siteManagerName || 'Not Assigned'}
                  </p>
                </div>

                {/* Site Supervisor */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Site Supervisor</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {site.siteSupervisor?.nickname || site.siteSupervisor?.fullName || site.siteSupervisorName || 'Not Assigned'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile FAB - shown only on mobile */}
      <div className="sm:hidden">
        <SiteQuickActionButtons
          site={site}
          phases={phases}
          userRole={user?.role}
          onStartPhase={handleStartPhase}
          onCompletePhase={handleCompletePhase}
          onSkipPhase={handleSkipPhase}
          onUpdateProgress={handleUpdateProgress}
          onAddPhase={handleAddPhase}
          onChangeStatus={handleChangeStatus}
          onAssignTeam={() => setAssignTeamModal(true)}
          isMobileFAB={true}
          // Attendance props for mobile FABs
          attendanceStatus={canEdit ? attendanceStatus : null}
          onCheckIn={() => setAttendanceModal({ open: true, type: 'check_in' })}
          onCheckOut={() => setAttendanceModal({ open: true, type: 'check_out' })}
        />
      </div>

      {/* Audit Trail Drawer */}
      {showHistoryPanel && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowHistoryPanel(false)} />
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <div className="w-screen max-w-lg">
              <div className="h-full flex flex-col bg-white shadow-xl">
                <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Activity History</h2>
                  <div className="flex items-center gap-2">
                    {/* Refresh Button */}
                    <button
                      onClick={() => { fetchHistory(); fetchAttendanceRecords(); }}
                      disabled={historyLoading}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center"
                      title="Refresh history"
                    >
                      <svg
                        className={`w-5 h-5 ${historyLoading ? 'animate-spin' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                    {/* Close Button */}
                    <button
                      onClick={() => setShowHistoryPanel(false)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  <ConsolidatedAuditTrail
                    history={history}
                    attendance={attendanceRecords}
                    site={site}
                    loading={historyLoading}
                    onAddNote={() => setAddNoteModal(true)}
                    onEditNote={() => setEditNotesModal(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <CompletePhaseModal
        isOpen={completePhaseModal.open}
        onClose={() => setCompletePhaseModal({ open: false, phase: null })}
        phase={completePhaseModal.phase}
        onConfirm={handleCompletePhaseConfirm}
        loading={actionLoading}
      />

      <SkipPhaseModal
        isOpen={skipPhaseModal.open}
        onClose={() => setSkipPhaseModal({ open: false, phase: null })}
        phase={skipPhaseModal.phase}
        onConfirm={handleSkipPhaseConfirm}
        loading={actionLoading}
      />

      <AddPhaseModal
        isOpen={addPhaseModal}
        onClose={() => setAddPhaseModal(false)}
        existingPhases={phases}
        templates={currentTemplates}
        onConfirm={handleAddPhaseConfirm}
        loading={actionLoading}
      />

      <UpdateProgressModal
        isOpen={updateProgressModal.open}
        onClose={() => setUpdateProgressModal({ open: false, phase: null })}
        phase={updateProgressModal.phase}
        onConfirm={handleUpdateProgressConfirm}
        loading={actionLoading}
      />

      <ChangeSiteStatusModal
        isOpen={changeStatusModal.open}
        onClose={() => setChangeStatusModal({ open: false, targetStatus: null })}
        currentStatus={site?.siteStatus}
        targetStatus={changeStatusModal.targetStatus}
        onConfirm={handleChangeStatusConfirm}
        loading={actionLoading}
      />

      <EditPhaseModal
        isOpen={editPhaseModal.open}
        onClose={() => setEditPhaseModal({ open: false, phase: null })}
        phase={editPhaseModal.phase}
        onConfirm={handleEditPhaseConfirm}
        onDelete={handleDeletePhase}
        loading={actionLoading}
      />

      <SetupPhasesModal
        isOpen={setupPhasesModal}
        onClose={() => setSetupPhasesModal(false)}
        site={site}
        existingPhases={phases}
        phaseTemplates={phaseTemplates}
        onConfirm={handleSetupPhasesConfirm}
        loading={actionLoading}
      />

      {/* Attendance Modal */}
      <AttendanceModal
        isOpen={attendanceModal.open}
        onClose={() => setAttendanceModal({ open: false, type: null })}
        siteId={id}
        type={attendanceModal.type}
        onSuccess={() => {
          setAttendanceRefresh(prev => prev + 1);
          fetchAttendanceStatus();
          fetchHistory();
          fetchAttendanceRecords();
        }}
      />

      {/* Assign Site Team Modal */}
      <AssignSiteTeamModal
        isOpen={assignTeamModal}
        onClose={() => setAssignTeamModal(false)}
        site={site}
        userRole={user?.role}
        onSuccess={() => {
          refreshData();
        }}
      />

      {/* Add Site Note Modal */}
      <AddSiteNoteModal
        isOpen={addNoteModal}
        onClose={() => setAddNoteModal(false)}
        onConfirm={handleAddNote}
        site={site}
        loading={actionLoading}
      />

      {/* Edit Site Notes Modal */}
      <EditSiteNotesModal
        isOpen={editNotesModal}
        onClose={() => setEditNotesModal(false)}
        onConfirm={handleEditNotes}
        site={site}
        loading={actionLoading}
      />
    </div>
  );
}
